/*
 * © 2026 Ecsper Ltd. All rights reserved.
 * SPDX-License-Identifier: LicenseRef-Ecsper-Proprietary
 * Unauthorised use, copying, or distribution is strictly prohibited.
 */

/**
 * Reads the current Word document as OOXML bytes via the Office Common API
 * `Document.getFileAsync` file slicing contract.
 *
 * @see https://learn.microsoft.com/javascript/api/office/office.document#getFileAsync_2_FileType_
 */
export async function getCompressedDocumentDocxBlob(): Promise<Blob> {
  return await new Promise((resolve, reject) => {
    Office.context.document.getFileAsync(
      Office.FileType.Compressed,
      { sliceSize: 4 * 1024 * 1024 },
      (result) => {
        if (result.status !== Office.AsyncResultStatus.Succeeded) {
          reject(new Error(result.error?.message ?? 'getFileAsync failed'));
          return;
        }

        const file = result.value;
        const chunks: Uint8Array[] = [];
        let sliceIndex = 0;

        const fail = (message: string): void => {
          file.closeAsync();
          reject(new Error(message));
        };

        const readNextSlice = (): void => {
          if (sliceIndex >= file.sliceCount) {
            file.closeAsync();
            resolve(mergeSlices(chunks));
            return;
          }

          file.getSliceAsync(sliceIndex, (sliceResult) => {
            if (sliceResult.status !== Office.AsyncResultStatus.Succeeded) {
              fail(sliceResult.error?.message ?? 'getSliceAsync failed');
              return;
            }

            const slice = sliceResult.value;
            try {
              if (sliceIndex === 0) {
                const d = slice.data;
                console.info(
                  '[Ecsper] getSliceAsync data type:',
                  d instanceof ArrayBuffer
                    ? 'ArrayBuffer'
                    : ArrayBuffer.isView(d)
                      ? 'ArrayBufferView'
                      : Array.isArray(d)
                        ? `Array (length: ${d.length})`
                        : typeof d === 'string'
                          ? `string (length: ${d.length})`
                          : typeof d,
                );
              }
              chunks.push(sliceDataToUint8(slice.data));
            } catch (err) {
              fail(err instanceof Error ? err.message : String(err));
              return;
            }

            sliceIndex += 1;
            readNextSlice();
          });
        };

        readNextSlice();
      },
    );
  });
}

export function sliceDataToUint8(data: unknown): Uint8Array {
  if (data instanceof ArrayBuffer) {
    return new Uint8Array(data);
  }
  if (ArrayBuffer.isView(data)) {
    return new Uint8Array(data.buffer, data.byteOffset, data.byteLength);
  }
  // Desktop Word (some builds): plain Array of byte values (0-255)
  if (Array.isArray(data)) {
    return new Uint8Array(data);
  }
  if (typeof data === 'string') {
    const binaryString = atob(data);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes;
  }
  throw new Error(`Unexpected slice data format from Office.File.getSliceAsync: ${typeof data}`);
}

function mergeSlices(chunks: readonly Uint8Array[]): Blob {
  const total = chunks.reduce((n, c) => n + c.byteLength, 0);
  const merged = new Uint8Array(total);
  let offset = 0;
  for (const c of chunks) {
    merged.set(c, offset);
    offset += c.byteLength;
  }
  return new Blob([merged], {
    type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  });
}
